import React from "react";
import API from "api";
import LocalHospitalOutlinedIcon from "@mui/icons-material/HealthAndSafety";
import Session from "helpers/session";
import {
  admittedColumn,
  memberColumn,
  lastMeasurmentColumn,
  breachColumn,
  heartRateColumn,
  weightColumn,
  temperatureColumn,
  spirometryColumn,
  pulseOxygenColumn,
  bloodGlucoseColumn,
  bloodPressureColumn,
  //column for activity
  activityColumn,
} from "./Main.components";
import moment from "moment";
import { memberObject } from "context/initialState/Member";
import { uMembers } from "helpers/translationStringConstants";
import { COORDINATOR } from "helpers/appConstants";
import { MEMBER_DISPATCHES } from "context/reducers/memberReducer";

export const loadMembersContext = async (memberDisp: any) => {
  const { items: allMembers } = await API.kore.members.getAll(
    Session.crackCookieToken(COORDINATOR),
    "scope=coordinator&"
  );
  memberDisp({ type: MEMBER_DISPATCHES.updateList, payload: allMembers });
  return allMembers;
};

export const loadConfigurationsContext = async (
  confDisp: any,
  organization: string
) => {
  const allConfigurations =
    await API.kore.configurations.getConfigurations(organization);
  const org = allConfigurations.organizationOptions.filter((data: any) => {
    return data.organization === organization;
  })[0];
  await confDisp({ type: "OPTIONS_UPLOAD", payload: allConfigurations });
  return org?.coordinators.filter((coord: any) => {
    return coord.id === Session.crackCookieUserId(COORDINATOR);
  })[0];
};

export const loadNotificationsContext = async (notifDisp: any) => {
  const { items: allNotifications } = await API.kore.notifications.getList(
    Session.crackCookieToken(COORDINATOR),
    "acknowledged:false"
  );
  notifDisp({ type: "UPDATE_COUNT", payload: allNotifications.length });
  notifDisp({ type: "UPDATE_NOTIFICATIONS", payload: allNotifications });
};

export const formatRows = async (
  memberList: memberObject[],
  memberDisp: any,
  memberRecents: any
) => {
  if (memberRecents) return memberRecents;
  const mappedPromises = memberList.map(async (member: memberObject) => {
    const { id, latestReading } = member;
    const { bp, spo2, hrt, bgl, spir, wt, temp } =
      await API.kore.readings.getRecentReadings(
        id,
        Session.crackCookieToken(COORDINATOR)
      );
    const recents = {
      bp: bp || "",
      spo2: spo2 || "",
      hrt: hrt || "",
      bgl: bgl || "",
      spir: spir || "",
      wt: wt || "",
      temp: temp || ""
    };
    return {
      id,
      member,
      lastMeasurement: {
        readingsArray: latestReading ? [latestReading] : []
      },
      recents
    };
  });

  const rowPromises = await Promise.all(mappedPromises);

  memberDisp({
    type: MEMBER_DISPATCHES.updateRecents,
    payload: rowPromises
  });
  return rowPromises;
};

export const createColumns = (
  translation: Function,
  goToReadings: Function,
  goToMemberPage: Function
) => [
  {
    field: "admitted",
    headerClassName: "main-member-list-header",
    headerName: <LocalHospitalOutlinedIcon />,
    width: 10,
    flex: 10,
    sortable: false,
    filterable: false,
    renderCell: (item: { row: { member: any } }) => {
      return admittedColumn(item.row.member);
    }
  },
  {
    field: "member",
    headerClassName: "main-member-list-header",
    headerName: translation(uMembers),
    width: 200,
    flex: 500,
    renderCell: (item: any) => {
      return memberColumn(item.row.member, goToMemberPage, item);
    },
    valueGetter: (item: {
      row: {
        member: {
          lastName: any;
          firstName: any;
          email: any;
          site: any;
          program: any;
          groups: any[];
          externId: any;
          hubIds: string | any[];
        };
      };
    }) =>
      `${item.row.member.lastName} ${item.row.member.firstName} ${
        item.row.member.email
      } ${item.row.member.site} ${item.row.member.program} ${
        item.row.member.groups[0]
      } ${item.row.member.externId} ${
        item.row.member.hubIds && item.row.member.hubIds.length > 0
          ? item.row.member.hubIds.toString()
          : ""
      }`
  },
  {
    field: "riskAlerts",
    headerClassName: "main-member-list-header",
    headerName: "Risk Alerts",
    width: 100,
    flex: 250,
    sortable: false,
    filterable: false,
    renderCell: (item: { row: { member: { latestReading: any } } }) => {
      return breachColumn(item.row.member.latestReading);
    }
  },
  {
    field: "bloodPressure",
    headerClassName: "main-member-list-header",
    headerName: "Blood Pressure",
    width: 100,
    flex: 300,
    sortable: false,
    filterable: false,
    renderCell: (item: any) => {
      return bloodPressureColumn(
        item.row.recents?.bp,
        item.row.member,
        goToReadings
      );
    }
  },
  {
    field: "pulseOxygen",
    headerClassName: "main-member-list-header",
    headerName: "Pulse Oxygen",
    width: 100,
    flex: 280,
    sortable: false,
    filterable: false,
    renderCell: (item: any) => {
      return pulseOxygenColumn(
        item.row.recents?.spo2,
        item.row.member,
        goToReadings
      );
    }
  },
  {
    field: "spirometry",
    headerClassName: "main-member-list-header",
    headerName: "Spirometry",
    width: 100,
    flex: 250,
    sortable: false,
    filterable: false,
    renderCell: (item: any) => {
      return spirometryColumn(
        item.row.recents?.spir,
        item.row.member,
        goToReadings
      );
    }
  },
  {
    field: "heartRate",
    headerClassName: "main-member-list-header",
    headerName: "Heart Rate",
    width: 100,
    flex: 250,
    sortable: false,
    filterable: false,
    renderCell: (item: any) => {
      return heartRateColumn(item.row.recents, item.row.member, goToReadings);
    }
  },
  {
    field: "temperature",
    headerClassName: "main-member-list-header",
    headerName: "Tempurature",
    width: 100,
    flex: 250,
    sortable: false,
    filterable: false,
    renderCell: (item: any) => {
      return temperatureColumn(
        item.row.recents?.temp,
        item.row.member,
        goToReadings
      );
    }
  },
  {
    field: "bloodGlucose",
    headerClassName: "main-member-list-header",
    headerName: "Blood Glucose",
    width: 100,
    flex: 280,
    sortable: false,
    filterable: false,
    renderCell: (item: any) => {
      return bloodGlucoseColumn(
        item.row.recents?.bgl,
        item.row.member,
        goToReadings
      );
    }
  },
  {
    field: "weight",
    headerClassName: "main-member-list-header",
    headerName: "Weight",
    width: 100,
    flex: 250,
    sortable: false,
    filterable: false,
    renderCell: (item: any) => {
      return weightColumn(item.row.recents?.wt, item.row.member, goToReadings);
    }
  },
  //add activity 
  {
    field: "activity",
    headerClassName: "main-member-list-header",
    headerName: "Activity",
    width: 100,
    flex: 250,
    sortable: false,
    filterable: false,
    renderCell: (item: any) => {
      // this will be implemented after activity is included in recentReadings
      // return activityColumn(item.row.recents?.steps, item.row.member, goToReadings);
      return activityColumn(item.row.lastMeasurement.readingsArray[0], item.row.member, goToReadings);

    }
  },
  {
    field: "lastMeasurement",
    headerClassName: "main-member-list-header",
    headerName: "Latest Reading",
    width: 260,
    flex: 500,
    filterable: false,
    renderCell: (params: { row: { lastMeasurement: any } }) => {
      return lastMeasurmentColumn(params.row.lastMeasurement);
    },
    valueGetter: (params: {
      row: {
        lastMeasurement: {
          readingsArray: { measuredAt: moment.MomentInput }[];
        };
      };
    }) =>
      params.row.lastMeasurement.readingsArray[0]
        ? moment(params.row.lastMeasurement.readingsArray[0].measuredAt)
        : moment("01-01-1900")
  }
];
